import { Geolocation } from '@capacitor/geolocation'
import { isNativePlatform } from '@/lib/platform'
import type { ClockLocation } from '@/hooks/useTimesheet'

/**
 * EP-154: the phone's position for a clock, read in two stages and warmed up
 * before the tap.
 *
 * A read is a pair that starts together: a low-accuracy one (cell and Wi-Fi,
 * a cached fix up to 5 minutes old is fine, 8 s limit) and a high-accuracy one
 * (GPS, 25 s limit). `fix()` hands back the high-accuracy fix when it is in,
 * else the first fix of either kind, and waits no longer than `WAIT_CAP` from
 * the moment it is called. The cap is a deadline of our own, because WebKit
 * can leave a read unanswered (measured on an iPhone 15 Pro, 2026-10-07: a
 * read started right after the camera sheet closes never answers, not even
 * with its timeout error).
 *
 * Only a refusal (PERMISSION_DENIED, or the plugin's denial in the native
 * app) is final and reported as 'denied'; everything else is 'unavailable'.
 *
 * In the operator app (Capacitor) the read and the permission go through
 * `@capacitor/geolocation`; on the web through `navigator.geolocation`.
 */
export class PositionError extends Error {
  readonly reason: 'denied' | 'unavailable' | 'cancelled'
  constructor(reason: 'denied' | 'unavailable' | 'cancelled') {
    super(reason)
    this.reason = reason
  }
}

export interface ReadOptions {
  enableHighAccuracy: boolean
  timeout: number
  maximumAge: number
}

export const LOW_READ: ReadOptions = { enableHighAccuracy: false, timeout: 8_000, maximumAge: 300_000 }
export const HIGH_READ: ReadOptions = { enableHighAccuracy: true, timeout: 25_000, maximumAge: 60_000 }
/** The longest the card waits for a position once the photo is back. */
export const WAIT_CAP = 30_000
/** A warm fix older than this is not used at the tap. */
export const WARM_MAX_AGE = 300_000

function toLocation(lat: number, lng: number, accuracy: number, timestamp: number): ClockLocation {
  return { lat, lng, accuracy, captured_at: new Date(timestamp).toISOString() }
}

function mapNativeError(e: unknown): PositionError {
  if (e instanceof PositionError) return e
  const message = e instanceof Error ? e.message : String(e)
  return new PositionError(/denied|permission|not authori[sz]ed/i.test(message) ? 'denied' : 'unavailable')
}

function readOnce(options: ReadOptions): Promise<ClockLocation> {
  if (isNativePlatform()) {
    return Geolocation.getCurrentPosition(options).then(
      (pos) => toLocation(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy, pos.timestamp),
      (e: unknown) => {
        throw mapNativeError(e)
      },
    )
  }
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new PositionError('unavailable'))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(toLocation(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy, pos.timestamp)),
      (err) => reject(new PositionError(err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable')),
      options,
    )
  })
}

/** True only when the permission is already granted; never prompts. */
export async function locationPermissionGranted(): Promise<boolean> {
  try {
    if (isNativePlatform()) {
      const p = await Geolocation.checkPermissions()
      return p.location === 'granted' || p.coarseLocation === 'granted'
    }
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return false
    return (await navigator.permissions.query({ name: 'geolocation' })).state === 'granted'
  } catch {
    return false
  }
}

// Native only: ask for the permission when it is not granted (this runs at the tap).
async function ensureNativePermission(): Promise<void> {
  try {
    const has = (p: { location: string; coarseLocation: string }) => p.location === 'granted' || p.coarseLocation === 'granted'
    if (has(await Geolocation.checkPermissions())) return
    if (has(await Geolocation.requestPermissions())) return
  } catch (e) {
    throw mapNativeError(e)
  }
  throw new PositionError('denied')
}

/** One quiet low-accuracy read for the warm-up. Call only when the permission is granted. */
export function readWarmPosition(): Promise<ClockLocation> {
  return readOnce(LOW_READ)
}

export function isFresh(fix: ClockLocation, now = Date.now()): boolean {
  if (!fix.captured_at) return false
  return now - new Date(fix.captured_at).getTime() < WARM_MAX_AGE
}

export interface PositionRead {
  /** The best fix in so far, or null. */
  peek(): ClockLocation | null
  /** The best fix, waiting for one for at most `cap` ms from now. */
  fix(cap?: number): Promise<ClockLocation>
  /** Rejects a pending `fix()` with reason 'cancelled'. */
  cancel(): void
}

export function startPositionRead(): PositionRead {
  let low: ClockLocation | null = null
  let high: ClockLocation | null = null
  let lowErr: PositionError | null = null
  let highErr: PositionError | null = null
  let cancelled = false
  const listeners = new Set<() => void>()
  const notify = () => [...listeners].forEach((l) => l())

  const gate = isNativePlatform() ? ensureNativePermission() : Promise.resolve()
  // A failed gate (denied) rejects both reads; the handlers below keep it handled.
  const run = (options: ReadOptions, ok: (f: ClockLocation) => void, fail: (e: PositionError) => void) => {
    gate
      .then(() => readOnce(options))
      .then(ok, (e: unknown) => fail(e instanceof PositionError ? e : new PositionError('unavailable')))
      .then(notify)
  }
  run(LOW_READ, (f) => { low = f }, (e) => { lowErr = e })
  run(HIGH_READ, (f) => { high = f }, (e) => { highErr = e })

  return {
    peek: () => high ?? low,
    fix: (cap = WAIT_CAP) =>
      new Promise<ClockLocation>((resolve, reject) => {
        const timer = window.setTimeout(() => finish(() => reject(new PositionError('unavailable'))), cap)
        const check = () => {
          if (cancelled) return finish(() => reject(new PositionError('cancelled')))
          const best = high ?? low
          if (best) return finish(() => resolve(best))
          if (lowErr?.reason === 'denied' || highErr?.reason === 'denied') return finish(() => reject(new PositionError('denied')))
          if (lowErr && highErr) return finish(() => reject(new PositionError('unavailable')))
        }
        const finish = (settle: () => void) => {
          window.clearTimeout(timer)
          listeners.delete(check)
          settle()
        }
        listeners.add(check)
        check()
      }),
    cancel: () => {
      cancelled = true
      notify()
    },
  }
}
